import React, { Fragment } from 'react'
import PropTypes from 'prop-types'

import clsx from 'clsx'
import { makeStyles } from '@mui/styles'
import Drawer from '@mui/material/Drawer'
import Panel from '@/components/layouts/panel/index'
import PanelContent from '@/components/layouts/panel/panel-content'
import BaseDrawerPanelHeader from './base-drawer-panel-header/base-drawer-panel-header'

const defaultPadding = 30

const useStyles = makeStyles(theme => ({
  container: {
    minWidth: 350,
    maxWidth: 'fit-content',
    borderRight: theme.border,
  },
  detail: {
    padding: defaultPadding,
    height: 'fit-content',
  },
  footer: {
    justifyContent: 'end',
    gap: 20,
    display: 'flex',
    height: 75,
    background: theme.backgroundColor,
    alignItems: 'center',
    padding: `0 ${defaultPadding}px`,
    width: '100%',
    '& > button': {
      width: 'fit-content',
    },
  },
  drawer: {
    display: 'flex',
    flexDirection: 'row',
    width: 'auto',
    minWidth: '350px',
    maxWidth: '90vw',
  },
  line: {
    width: '100%',
    height: 1,
    backgroundColor: theme.borderColor,
  },
}))

const BaseDrawer = (props) => {
  const classes = useStyles(props)
  const {
    anchor = 'right',
    withoutScrolling = false,
    perfectScrollbarOptions = {},
    withoutFooter = false,
    withSecondFooter = false,
    showHeader = true,
    hideBackdrop = false,
    open,
    onClose,
    children,
    className,
    classNameSecondView,
    headerChildren,
    footerChildren,
    secondFooterChildren,
    headerChildrenClass,
    childrenClass,
    footerChildrenClass,
    showCloseIcon = true,
    showCollapseIcon,
    enableBackdropClick = true,
    withFooterLine = false,
    panelContentRef,
    secondView,
    styles = {},
    modalProps,
    paperClass,
    rootClass,
    paperProps = {},
    showHeaderWithoutTitle,
    dataQsId,
  } = props

  return (
    <Drawer
      classes={{
        root: clsx(styles.root, rootClass),
        paper: clsx(classes.drawer, styles.drawer, paperClass),
      }}
      anchor={anchor}
      transitionDuration={400}
      disableEnforceFocus
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' || enableBackdropClick) {
          onClose && onClose()
        }
      }}
      PaperProps={paperProps}
      ModalProps={{
        disableEscapeKeyDown: true,
        container: document.fullscreenElement ?? document.body,
        ...modalProps,
      }}
      slotProps={{ backdrop: { invisible: hideBackdrop } }}
      data-qs-id={dataQsId}
    >
      {secondView && (
        <Panel className={clsx(classes.container, classNameSecondView)} >
          {secondView}
        </Panel>
      )}
      <Panel className={clsx(classes.container, className)} >
        {(showHeader && headerChildren) || showHeaderWithoutTitle
          ? <BaseDrawerPanelHeader className={headerChildrenClass} showCloseIcon={showCloseIcon} showCollapseIcon={showCollapseIcon} onClose={onClose} children={headerChildren} />
          : null
        }
        <PanelContent
          className={clsx(classes.detail, childrenClass)}
          ref={panelContentRef}
          usePerfectScrollbar={!withoutScrolling}
          perfectScrollbarOptions={perfectScrollbarOptions}
        >
          {children}
        </PanelContent>
        {
          !withoutFooter && (
            <>
              {withSecondFooter && <>
                {secondFooterChildren.map((child, index) => (
                  <Fragment key={index}>
                    {withFooterLine ? <div className={classes.line} /> : null}
                    <div className={clsx(classes.footer, footerChildrenClass)}>
                      {child}
                    </div>
                  </Fragment>
                ))}
              </>}
              {withFooterLine ? <div className={classes.line} /> : null}
              <div className={clsx(classes.footer, footerChildrenClass)}>
                {footerChildren}
              </div>
            </>
          )
        }
      </Panel>
    </Drawer>
  )
}

BaseDrawer.propTypes = {
  anchor: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  className: PropTypes.string,
  headerChildren: PropTypes.node,
  headerChildrenClass: PropTypes.string,
  children: PropTypes.node,
  childrenClass: PropTypes.string,
  footerChildren: PropTypes.node,
  secondFooterChildren: PropTypes.node,
  footerChildrenClass: PropTypes.string,
  enableBackdropClick: PropTypes.bool,
  showCloseIcon: PropTypes.bool,
  showCollapseIcon: PropTypes.bool,
  showHeader: PropTypes.bool,
  panelContentRef: PropTypes.any,
  withFooterLine: PropTypes.bool,
  withoutFooter: PropTypes.bool,
  withSecondFooter: PropTypes.bool,
  withoutScrolling: PropTypes.bool,
  secondView: PropTypes.node,
  classNameSecondView: PropTypes.string,
  styles: PropTypes.object,
  hideBackdrop: PropTypes.bool,
  paperClass: PropTypes.string,
  rootClass: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  paperProps: PropTypes.object,
  showHeaderWithoutTitle: PropTypes.bool,
  dataQsId: PropTypes.string.isRequired,
  perfectScrollbarOptions: PropTypes.object,
}

export default BaseDrawer
